import { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';

export default function AdminLaxativesPage() {
  const [laxatives, setLaxatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchLaxatives();
  }, []);

  const fetchLaxatives = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/laxatives');
      if (data.success) setLaxatives(data.data);
    } catch (error) {
      console.error('Failed to fetch laxatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditing({ name: '', isActive: true });
    setModalOpen(true);
  };

  const handleEdit = (laxative) => {
    setEditing({ ...laxative });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this laxative? Doctors already referencing it will keep the name as free text.')) return;
    try {
      await axios.delete(`/api/admin/laxatives/${id}`);
      fetchLaxatives();
    } catch (error) {
      console.error('Failed to delete laxative:', error);
      alert('Failed to delete laxative: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSave = async () => {
    if (!editing.name?.trim()) {
      alert('Name is required');
      return;
    }

    const payload = {
      name: editing.name.trim(),
      isActive: editing.isActive,
    };

    try {
      if (editing.id) {
        await axios.put(`/api/admin/laxatives/${editing.id}`, payload);
      } else {
        await axios.post('/api/admin/laxatives', payload);
      }
      setModalOpen(false);
      setEditing(null);
      fetchLaxatives();
    } catch (error) {
      console.error('Failed to save laxative:', error);
      alert('Failed to save laxative: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) return <Loading>Loading...</Loading>;

  return (
    <Container>
      <Header>
        <h1>Laxatives</h1>
        <AddButton onClick={handleCreate}>+ Add Laxative</AddButton>
      </Header>

      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Active</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {laxatives.map(laxative => (
            <Tr key={laxative.id}>
              <Td>{laxative.name}</Td>
              <Td>{laxative.isActive ? 'Yes' : 'No'}</Td>
              <Td align="right">
                <ActionGroup>
                  <TableButton onClick={() => handleEdit(laxative)}>Edit</TableButton>
                  <TableButton danger onClick={() => handleDelete(laxative.id)}>Delete</TableButton>
                </ActionGroup>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {laxatives.length === 0 && <EmptyState>No laxatives found.</EmptyState>}

      {modalOpen && editing && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editing.id ? 'Edit Laxative' : 'Add Laxative'}</h2>
              <CloseButton onClick={() => setModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label>Name</label>
                <Input
                  value={editing.name || ''}
                  onChange={e => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. MiraLAX"
                  autoFocus
                />
              </FormField>
              <FormField inline>
                <input
                  type="checkbox"
                  checked={editing.isActive}
                  onChange={e => setEditing({ ...editing, isActive: e.target.checked })}
                />
                <label>Active</label>
              </FormField>
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => setModalOpen(false)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    margin: 0;
    font-size: 1.5rem;
  }
`;

const AddButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f02a4c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #d12442;
  }
`;

const Loading = styled.div`
  padding: 2rem;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #666;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: ${props => props.align || 'left'};
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 0.85rem;
  color: #555;
  border-bottom: 1px solid #e9ecef;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #e9ecef;
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  text-align: ${props => props.align || 'left'};
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const TableButton = styled.button`
  padding: 0.35rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  background: ${props => props.danger ? '#fee2e2' : '#e0e7ff'};
  color: ${props => props.danger ? '#991b1b' : '#3730a3'};

  &:hover {
    background: ${props => props.danger ? '#fecaca' : '#c7d2fe'};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 420px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;

  h2 {
    margin: 0;
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: ${props => props.inline ? 'inline' : 'block'};
    margin-left: ${props => props.inline ? '0.5rem' : '0'};
    margin-bottom: ${props => props.inline ? '0' : '0.35rem'};
    font-weight: 600;
    font-size: 0.85rem;
    color: #444;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const PrimaryButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f02a4c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #d12442;
  }
`;

const SecondaryButton = styled.button`
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #e5e7eb;
  }
`;
